import React, { FC, HTMLAttributes, useCallback, useState } from 'react';
import { Divider, Input, InputNumber, Popover, Select, Space, Typography } from 'antd';
import {
  ArrowRightOutlined,
  CheckSquareOutlined,
  ColumnHeightOutlined,
  LineChartOutlined,
  ToTopOutlined,
  VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import './ProgressConfigInputStyle.less';
import Utils from '../../../utils/Utils';

const { Text } = Typography;

export type ProgressConfigType = 'Start and Target Value' | 'Open/Done' | 'Threshold';
const ProgressConfigTypes: [string, React.ComponentType][] = [
  ['Start and Target Value', LineChartOutlined],
  ['Open/Done', CheckSquareOutlined],
  ['Threshold', ColumnHeightOutlined],
];
export type ProgressConfigThresholdType = 'Stay Below' | 'Stay Above';

export type ProgressConfig = {
  type: ProgressConfigType;
  startValue: number;
  endValue: number;
  threshold: number;
  thresholdType: ProgressConfigThresholdType;
  unit: string;
};

export const DEFAULT_PROGRESS_CONFIG: ProgressConfig = {
  type: 'Start and Target Value',
  startValue: 0,
  endValue: 100,
  unit: '%',
  threshold: 0,
  thresholdType: 'Stay Above',
};

type Props = {
  value?: ProgressConfig;
  onChange?: (value?: ProgressConfig) => void;
  mode?: 'edit' | 'view';
};

type ProgressConfigPopoverContentProps = {
  value: ProgressConfig;
  onChange: (value: ProgressConfig) => void;
};

const LabeledItem: FC<{ title: string } & HTMLAttributes<HTMLDivElement>> = (props) => {
  return (
    <div {...props} className={Utils.mergeClasses('progress-config-labeled-item')}>
      <Text type="secondary" style={{ fontSize: 13, marginLeft: 13 }}>
        {props.title}
      </Text>
      {props.children}
    </div>
  );
};

const ProgressConfigPopoverContent: FC<ProgressConfigPopoverContentProps> = (props) => {
  const { value, onChange } = props;
  const handleChange = useCallback(
    (field: string, val: any) => {
      const v: any = { ...value };
      v[field] = val;
      onChange?.(v);
    },
    [onChange, value],
  );
  return (
    <div style={{ width: 330 }}>
      <LabeledItem title="Type">
        <Select<ProgressConfigType>
          onMouseDown={(e) => e.preventDefault()}
          bordered={false}
          showArrow={true}
          // suffixIcon={null}
          value={value.type}
          onChange={(val) => handleChange('type', val)}>
          {ProgressConfigTypes.map(([item, Comp], key) => (
            <Select.Option key={key} value={item}>
              <Comp />
              <span> {item}</span>
            </Select.Option>
          ))}
        </Select>
      </LabeledItem>

      {value?.type !== 'Open/Done' && (
        <>
          <Divider style={{ margin: '4px 0' }} />
          <Space>
            {value?.type === 'Start and Target Value' && (
              <LabeledItem title="Start">
                <InputNumber
                  value={value.startValue}
                  onChange={(v) => handleChange('startValue', v)}
                  className="labeled-input labeled-input-number"
                />
              </LabeledItem>
            )}
            {value?.type === 'Start and Target Value' && (
              <LabeledItem title="Target">
                <InputNumber
                  value={value.endValue}
                  onChange={(v) => handleChange('endValue', v)}
                  className="labeled-input labeled-input-number"
                />
              </LabeledItem>
            )}
            {value?.type === 'Threshold' && (
              <LabeledItem title="Threshold">
                <InputNumber
                  value={value.threshold}
                  onChange={(v) => handleChange('threshold', v)}
                  className="labeled-input labeled-input-number"
                />
              </LabeledItem>
            )}
            <LabeledItem title="Unit">
              <Input
                value={value.unit}
                onChange={(v) => handleChange('unit', v.target.value)}
                className="labeled-input labeled-input-number"
              />
            </LabeledItem>
            {value?.type === 'Threshold' && (
              <LabeledItem title="Type">
                <Select
                  value={value.thresholdType}
                  onChange={(v) => handleChange('thresholdType', v)}
                  className="labeled-input labeled-input-select">
                  {['Stay Above', 'Stay Below'].map((item, key) => (
                    <Select.Option key={key} value={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </LabeledItem>
            )}
          </Space>
          {value?.type === 'Start and Target Value' && value.startValue === value.endValue && (
            <Typography style={{ color: 'red', paddingTop: 10 }}>
              The start and target value must not be the same.
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export const ProgressConfigValue: FC<{ value: ProgressConfig }> = (props) => {
  const { value } = props;
  return (
    <Space>
      {value.type === 'Threshold' && value.thresholdType === 'Stay Above' && (
        <ToTopOutlined style={{ color: 'black' }} />
      )}
      {value.type === 'Threshold' && value.thresholdType === 'Stay Below' && (
        <VerticalAlignBottomOutlined style={{ color: 'black' }} />
      )}
      <Text style={{ width: 500 }}>
        {value.type === 'Open/Done' ? (
          <>
            <CheckSquareOutlined /> Open/Done
          </>
        ) : value.type === 'Start and Target Value' ? (
          <>
            <LineChartOutlined /> {value.startValue} {value.unit} <ArrowRightOutlined />{' '}
            {value.endValue} {value.unit}
          </>
        ) : (
          `${value.thresholdType} ${value.threshold} ${value.unit}`
        )}
      </Text>
    </Space>
  );
};

export const ProgressConfigInput: FC<Props> = (props) => {
  const { value, onChange, mode } = props;
  const [tempValue, setTempValue] = useState<ProgressConfig>();
  const [visible, setVisible] = useState<boolean>(false);
  if (mode === 'edit') {
    return (
      <Popover
        content={
          <ProgressConfigPopoverContent
            value={tempValue ?? DEFAULT_PROGRESS_CONFIG}
            onChange={(v) => setTempValue(v)}
          />
        }
        trigger="click"
        onOpenChange={(visible) => {
          setVisible(visible);
          if (visible) {
            setTempValue(value);
          } else {
            onChange?.(tempValue);
          }
        }}>
        <div className="progress-config-input">
          <ProgressConfigValue value={(visible ? tempValue : value) ?? DEFAULT_PROGRESS_CONFIG} />
        </div>
      </Popover>
    );
  } else {
    return (
      <div className="progress-config-input">
        <ProgressConfigValue value={(visible ? tempValue : value) ?? DEFAULT_PROGRESS_CONFIG} />
      </div>
    );
  }
};
